import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronRight, ArrowLeft, Bot, Users, LayoutGrid, Activity, MessageSquare, X, Circle } from 'lucide-react';
import { AgentDetailCard, type AgentPulseLite } from './AgentDetailCard';
import { GlassCard } from './SharedUI';
import { apiFetch } from '../services/apiClient';

export type DrillLevel = 'overview' | 'org' | 'agent-list' | 'agent';

export interface DrillState {
  level: DrillLevel;
  agentId?: string;
}

interface NexusDrilldownProps {
  t: any;
  orgConnection?: any;
  workDomain?: 'personal' | 'work';
  pulses: Map<string, AgentPulseLite>;
  drill: DrillState;
  setDrill: (d: DrillState) => void;
  onSelectAgent: (agent: any) => void;
  onCloseAll: () => void;
}

function relativeTime(iso?: string | null): string {
  if (!iso) return '从未';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '未知';
  const diff = Date.now() - ts;
  if (diff < 1000) return '刚刚';
  if (diff < 60000) return `${Math.floor(diff / 1000)} 秒前`;
  if (diff < 3600000) return `${Math.floor(diff / 60000)} 分钟前`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} 小时前`;
  return `${Math.floor(diff / 86400000)} 天前`;
}

function statusColor(status?: string): string {
  switch (status) {
    case 'active': return 'text-emerald-400';
    case 'busy': return 'text-amber-400';
    case 'idle': return 'text-sky-400';
    case 'terminated': return 'text-white/40';
    default: return 'text-white/40';
  }
}

export function NexusDrilldown({ t, orgConnection, pulses, drill, setDrill, onSelectAgent, onCloseAll, workDomain }: NexusDrilldownProps) {
  const [agents, setAgents] = useState<any[]>([]);
  const [resolvedOrgName, setResolvedOrgName] = useState<string>('');
  // Bug 33: 添加 loading 状态
  const [agentsLoading, setAgentsLoading] = useState(true);

  const isWork = workDomain === 'work' && Boolean(orgConnection?.connected && orgConnection?.orgId);

  useEffect(() => {
    let active = true;
    setAgentsLoading(true); // Bug 33: 设置 loading 状态
    apiFetch('/api/agents')
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { if (active) setAgents(Array.isArray(d) ? d : d.agents || []); })
      .catch(() => {})
      .finally(() => { if (active) setAgentsLoading(false); }); // Bug 33: 重置 loading 状态
    return () => { active = false; };
  }, []);

  // C1：拉取真实组织名（/api/auth/orgs 返回用户所属组织，含 name 字段）
  useEffect(() => {
    if (workDomain !== 'work' || !orgConnection?.orgId) return;
    let active = true;
    apiFetch('/api/auth/orgs')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!active || !d?.orgs) return;
        const org = (d.orgs as any[]).find((o: any) => o.orgId === orgConnection.orgId);
        if (org?.name) setResolvedOrgName(org.name);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [workDomain, orgConnection?.orgId]);

  const selectedAgent = useMemo(
    () => (drill.agentId ? agents.find((a) => a.id === drill.agentId) : undefined),
    [agents, drill.agentId],
  );

  const summary = useMemo(() => {
    const total = agents.length;
    let activeCount = 0;
    let busyCount = 0;
    let messageBurst = 0;
    for (const a of agents) {
      const p = pulses.get(a.id);
      if (p?.status === 'active' || p?.active) activeCount++;
      if (p?.status === 'busy') busyCount++;
      if (p && p.recentMessages > 0) messageBurst++;
    }
    const byCategory = new Map<string, number>();
    for (const a of agents) {
      const c = a.category || '未分类';
      byCategory.set(c, (byCategory.get(c) || 0) + 1);
    }
    return { total, activeCount, busyCount, messageBurst, byCategory: [...byCategory.entries()] };
  }, [agents, pulses]);

  // Esc 逐级返回
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (drill.level === 'agent') setDrill({ level: 'agent-list' });
      else if (drill.level === 'agent-list') setDrill({ level: 'org' });
      else if (drill.level === 'org') onCloseAll();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drill.level, setDrill, onCloseAll]);

  if (drill.level === 'overview') return null;

  const crumbs: { key: DrillLevel; label: string }[] = [
    { key: 'org', label: isWork ? (resolvedOrgName || orgConnection?.orgName || '组织中枢') : '个人智能体集群' },
    { key: 'agent-list', label: t.agentList || '智能体列表' },
    { key: 'agent', label: selectedAgent?.name || selectedAgent?.label || '智能体详情' },
  ].filter((c) => c.key !== 'overview');

  const goTo = (level: DrillLevel) => {
    if (level === 'agent' && !drill.agentId) return;
    setDrill({ level, agentId: level === 'agent' ? drill.agentId : undefined });
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 40 }}
      transition={{ duration: 0.3 }}
      className="absolute right-8 top-24 w-[22rem] max-h-[calc(100vh-12rem)] flex flex-col z-40 pointer-events-auto"
    >
      <GlassCard className="flex flex-col rounded-[1.5rem] border-white/5 bg-black/40 backdrop-blur-3xl overflow-hidden h-full">
        {/* 面包屑 */}
        <div className="flex items-center gap-1 px-4 py-3 border-b border-white/5 text-[11px]">
          <button onClick={onCloseAll} className="flex items-center gap-1 text-white/40 hover:text-white/80 transition-colors">
            <Circle size={8} className="text-celestial-saturn" /> 中枢总览
          </button>
          {crumbs
            .filter((c) => c.key === drill.level || (drill.level === 'agent' && c.key === 'agent-list'))
            .map((c) => (
              <React.Fragment key={c.key}>
                <ChevronRight size={12} className="text-white/25" />
                <button
                  onClick={() => goTo(c.key)}
                  className={`truncate max-w-[8rem] ${c.key === drill.level ? 'text-celestial-saturn font-bold' : 'text-white/50 hover:text-white/80'}`}
                >
                  {c.label}
                </button>
              </React.Fragment>
            ))}
          <button onClick={onCloseAll} className="ml-auto text-white/40 hover:text-white transition-colors" aria-label="关闭">
            <X size={14} />
          </button>
        </div>

        <div className="overflow-y-auto px-4 py-4 space-y-4 flex-1">
          <AnimatePresence mode="wait">
            {/* 组织 / 个人集群层 */}
            {drill.level === 'org' && (
              <motion.div key="org" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-celestial-mars to-celestial-saturn flex items-center justify-center">
                    <Users size={20} className="text-white" />
                  </div>
                  <div>
                  <div className="text-sm font-black text-white/90">{isWork ? (resolvedOrgName || orgConnection?.orgName || '组织中枢') : '个人智能体集群'}</div>
                  <div className="text-[11px] text-white/45">{isWork ? '组织域 · 协同工作' : '个人域 · 私有智能体'}</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <Stat label="总数" value={summary.total} icon={<Bot size={12} />} />
                  <Stat label="活跃" value={summary.activeCount} icon={<Activity size={12} className="text-emerald-400" />} />
                  <Stat label="消息爆发" value={summary.messageBurst} icon={<MessageSquare size={12} className="text-amber-400" />} />
                </div>

                <div className="space-y-1.5">
                  <div className="text-[11px] text-white/45 px-1">{t.byCategory || '按分类'}</div>
                  {summary.byCategory.map(([cat, count]) => (
                    <div key={cat} className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-2 text-xs">
                      <span className="text-white/70">{cat}</span>
                      <span className="font-mono text-white/60">{count}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => goTo('agent-list')}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-celestial-saturn/20 hover:bg-celestial-saturn/30 border border-celestial-saturn/30 text-celestial-saturn text-xs font-black tracking-wider transition-colors"
                >
                  <LayoutGrid size={14} /> {t.viewAgents || '查看智能体列表'}
                </button>
              </motion.div>
            )}

            {/* 智能体列表层 */}
            {drill.level === 'agent-list' && (
              <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-2">
                <button onClick={() => setDrill({ level: 'org' })} className="flex items-center gap-1 text-[11px] text-white/45 hover:text-white/80 transition-colors mb-1">
                  <ArrowLeft size={12} /> {t.back || '返回'}
                </button>
                {agents.map((a) => {
                  const p = pulses.get(a.id);
                  const status = p?.status ?? a.status;
                  return (
                    <button
                      key={a.id}
                      onClick={() => { onSelectAgent(a); setDrill({ level: 'agent', agentId: a.id }); }}
                      className="w-full flex items-center gap-3 rounded-xl bg-white/5 hover:bg-white/10 px-3 py-2.5 text-left transition-colors"
                    >
                      <Circle size={9} className={`${statusColor(status)} ${p?.active || status === 'active' || status === 'busy' ? 'animate-pulse' : ''}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-bold text-white/85 truncate">{a.name || a.label}</div>
                        <div className="text-[10px] text-white/40 truncate">{a.category || '未分类'}</div>
                      </div>
                      {p && p.recentMessages > 0 && (
                        <span className="text-[10px] font-mono text-emerald-400">+{p.recentMessages}</span>
                      )}
                    </button>
                  );
                })}
                {agents.length === 0 && <div className="text-center text-xs text-white/40 py-6">{agentsLoading ? '加载中...' : '暂无智能体'}</div>}
              </motion.div>
            )}

            {/* 单智能体详情层 */}
            {drill.level === 'agent' && selectedAgent && (
              <motion.div key="agent" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">
                <button onClick={() => setDrill({ level: 'agent-list' })} className="flex items-center gap-1 text-[11px] text-white/45 hover:text-white/80 transition-colors">
                  <ArrowLeft size={12} /> {t.back || '返回'}
                </button>
                <AgentDetailCard
                  agent={selectedAgent}
                  pulse={pulses.get(selectedAgent.id)}
                  memoryCount={selectedAgent?.memoryCount}
                  onClose={onCloseAll}
                  onChat={onSelectAgent}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </GlassCard>
    </motion.div>
  );
}

function Stat({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-white/5 px-2 py-2.5 text-center">
      <div className="flex items-center justify-center gap-1 text-white/40 text-[10px]">{icon}{label}</div>
      <div className="text-lg font-black text-white/90 mt-0.5">{value}</div>
    </div>
  );
}
